import React, { useEffect, useState } from 'react';
import { Card, Avatar, Button, Divider, Typography, Table, Space, message, Form, Input, Row, Col, DatePicker } from 'antd';
import { UserOutlined, KeyOutlined, CameraOutlined, EyeInvisibleOutlined, LaptopOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { logAPIWithErrorHandling } from '../../../api/logs/log';
import dayjs from 'dayjs';
import { userAPIWithErrorHandling } from '../../../api/User/infoUser';

const { Title, Text } = Typography;

export default function AccountManagement() {
    const [activeTab, setActiveTab] = useState('info');
    const [logLoading, setLogLoading] = useState(false);
    const [loginSessions, setLoginSessions] = useState([]);
    const [infoLoading, setInfoLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);

    const [infoForm] = Form.useForm(); 
    const [passwordForm] = Form.useForm();

    const { user } = useSelector((state) => state.persistedReducer.user);

    // Load initial data vào form
    useEffect(() => {
        const data = user?.data;
        if (data) {
            infoForm.setFieldsValue({
                name: data?.info?.name,
                dob: data?.info?.dob ? dayjs(data?.info?.dob) : null,
                phone: data?.phone, 
                email: data?.email,
                org: data?.info?.org,
                address: data?.info?.address
            });
        }
    }, [user, infoForm]);

    const fetchLogs = async () => {
        try {
            setLogLoading(true);
            const response = await logAPIWithErrorHandling.getLoginAccount();
            if (response.data.success) {
                setLoginSessions(response.data.data || []);
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLogLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab !== 'info') {
            fetchLogs();
        }
    }, [activeTab]);

    // Xử lý submit cập nhật thông tin cơ bản
    const handleUpdateInfo = async (values) => {
        try {
            setInfoLoading(true);
            
            const payload = {
                name: values.name,
                dob: values.dob ? values.dob.format('YYYY-MM-DD') : null,
                email: values.email,
                phone: values.phone,
                org: values.org,
                address: values.address
            };
            
            await userAPIWithErrorHandling.updateProfile(payload);
            
            // Giả lập API call để test
            // await new Promise(resolve => setTimeout(resolve, 1000));
            
            message.success('Cập nhật thông tin thành công!');
        } catch (error) {
            message.error(error.message || 'Cập nhật thông tin thất bại!');
        } finally {
            setInfoLoading(false);
        }
    };

    // Xử lý submit đổi mật khẩu
    const handleChangePassword = async (values) => {
        try {
            setPasswordLoading(true);
            
            const payload = {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword
            };
            
            await userAPIWithErrorHandling.changePassword(payload);
            
            // Giả lập API call để test
            // await new Promise(resolve => setTimeout(resolve, 1000));
            
            message.success('Đổi mật khẩu thành công!');
            passwordForm.resetFields();
            
        } catch (error) {
            message.error(error.message || 'Đổi mật khẩu thất bại!');
        } finally {
            setPasswordLoading(false);
        }
    };

    const columns = [
        {
            title: 'Thiết bị',
            dataIndex: 'user_agent',
            key: 'user_agent',
            width: '60%',
            render: (text) => (
                <Space>
                    <LaptopOutlined style={{ fontSize: 18 }} />
                    <div>
                        <div style={{ fontWeight: 500 }}>{text}</div>
                    </div>
                </Space>
            )
        },
        {
            title: 'IP Address',
            dataIndex: 'ip_address',
            key: 'ip_address',
            width: '20%'
        },
        {
            title: 'Thời gian',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '20%',
            render: (text) => (
                <Space>
                    <ClockCircleOutlined />
                    {text ? dayjs(text).format('DD/MM/YYYY HH:mm:ss') : '-'}
                </Space>
            )
        }
    ];

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            {/* Header Card */}
            <Card style={{ maxWidth: 1200, margin: '0 auto 24px', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24 }}>
                    <div style={{ position: 'relative' }}>
                        <Avatar size={80} icon={<UserOutlined />} style={{ background: '#CBD5E1' }} />
                        <Button
                            type="primary"
                            shape="circle"
                            size="small"
                            icon={<CameraOutlined />}
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                background: '#10b981',
                                borderColor: '#10b981'
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Title level={4} style={{ margin: '0 0 4px' }}>
                            {user?.data?.info?.name || 'taikhoan'}
                        </Title>
                        <div style={{ marginTop: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {user?.data?.email || 'taikhoan@gmail.com'}
                            </Text>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Tabs Navigation */}
            <div style={{ maxWidth: 1200, margin: '0 auto 16px' }}>
                <Space size="middle">
                    <Button
                        type={activeTab === 'info' ? 'primary' : 'default'}
                        onClick={() => setActiveTab('info')}
                        icon={<UserOutlined />}
                        style={activeTab === 'info' ? { background: '#10b981', borderColor: '#10b981' } : {}}
                    >
                        Thông tin tài khoản
                    </Button>
                    <Button
                        type={activeTab === 'sessions' ? 'primary' : 'default'}
                        onClick={() => setActiveTab('sessions')}
                        icon={<LaptopOutlined />}
                        style={activeTab === 'sessions' ? { background: '#10b981', borderColor: '#10b981' } : {}}
                    >
                        Phiên đăng nhập
                    </Button>
                </Space>
            </div>

            {/* Content Card */}
            <Card style={{ maxWidth: 1200, margin: '0 auto', borderRadius: 8 }} bodyStyle={{ padding: '32px' }}>
                {activeTab === 'info' ? (
                    <>
                        {/* Form thông tin cơ bản */}
                        <Title level={5} style={{ marginBottom: 8 }}>
                            <UserOutlined style={{ marginRight: 8 }} />
                            Thông tin cơ bản
                        </Title>

                        <Form
                            form={infoForm}
                            layout="vertical"
                            onFinish={handleUpdateInfo}
                            style={{ marginBottom: 32 }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        label="Họ và tên"
                                        name="name"
                                        rules={[
                                            { required: true, message: 'Vui lòng nhập tên chủ tài khoản!' },
                                            { min: 2, message: 'Tên chủ tài khoản phải có ít nhất 2 ký tự!' }
                                        ]}
                                    >
                                        <Input placeholder="Nhập tên chủ tài khoản" />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item label="Ngày sinh" name="dob">
                                        <DatePicker 
                                            style={{ width: '100%' }} 
                                            format="DD/MM/YYYY"
                                            placeholder="Chọn ngày sinh"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        label="Email"
                                        name="email"
                                    >
                                        <Input placeholder="Nhập email" />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item 
                                        label="Số điện thoại" 
                                        name="phone"
                                        rules={[
                                            { required: true, message: 'Vui lòng nhập số điện thoại!' },
                                        ]}
                                    >
                                        <Input placeholder="Nhập số điện thoại" disabled />
                                    </Form.Item>
                                </Col>
                                
                                { (user?.data?.info?.type === 'SHOP' || user?.data?.info?.type === 'WARD_LEADER')  && (
                                <>
                                    <Col xs={24} sm={24} md={24}>
                                        <Form.Item
                                            label="Thông tin đơn vị"
                                            name="org"
                                            rules={[
                                                { required: false, message: 'Vui lòng nhập thông tin đơn vị!' },
                                                { min: 2, message: 'Tên chủ tài khoản phải có ít nhất 2 ký tự!' }
                                            ]}
                                        >
                                            <Input placeholder="Nhập thông tin đơn vị" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={24} md={24}>
                                        <Form.Item
                                            label="Địa chỉ"
                                            name="address"
                                            rules={[
                                                { required: false, message: 'Vui lòng nhập địa chỉ!' },
                                                { min: 2, message: 'Địa chỉ phải có ít nhất 2 ký tự!' }
                                            ]}
                                        >
                                            <Input placeholder="Nhập địa chỉ" />
                                        </Form.Item>
                                    </Col>
                                </>
                                )}
                            </Row>

                            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={infoLoading}
                                    style={{
                                        background: '#10b981',
                                        borderColor: '#10b981'
                                    }}
                                >
                                    Lưu cài đặt
                                </Button>
                            </Form.Item>
                        </Form>

                        <Divider />

                        {(user?.data?.info?.type === 'STAFF' || user?.data?.info?.type === 'BRANCH_MANAGER') && (
                        <>
                        {/* Form đổi mật khẩu */}
                        <Title level={5} style={{ marginBottom: 8 }}>
                            <KeyOutlined style={{ marginRight: 8 }} />
                            Đổi mật khẩu
                        </Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24, fontSize: 12 }}>
                            Thay đổi mật khẩu thường xuyên giúp tăng cường bảo mật của tài khoản và giảm thiểu rủi ro từ bên ngoài
                        </Text>

                        <Form
                            form={passwordForm}
                            layout="vertical"
                            onFinish={handleChangePassword}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        label="Mật khẩu hiện tại"
                                        name="currentPassword"
                                        rules={[{ required: true, message: 'Vui lòng nhập mật khẩu hiện tại!' }]}
                                    >
                                        <Input.Password 
                                            placeholder="Nhập mật khẩu hiện tại"
                                            iconRender={(visible) => <EyeInvisibleOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        label="Mật khẩu mới"
                                        name="newPassword"
                                        rules={[
                                            { required: true, message: 'Vui lòng nhập mật khẩu mới!' },
                                            { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự!' }
                                        ]}
                                    >
                                        <Input.Password 
                                            placeholder="Nhập mật khẩu mới"
                                            iconRender={(visible) => <EyeInvisibleOutlined />}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        label="Xác nhận mật khẩu mới"
                                        name="confirmPassword"
                                        dependencies={['newPassword']}
                                        rules={[
                                            { required: true, message: 'Vui lòng xác nhận mật khẩu mới!' },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('newPassword') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
                                                }
                                            })
                                        ]}
                                    >
                                        <Input.Password 
                                            placeholder="Nhập lại mật khẩu mới"
                                            iconRender={(visible) => <EyeInvisibleOutlined />}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={passwordLoading}
                                    style={{
                                        background: '#10b981',
                                        borderColor: '#10b981'
                                    }}
                                >
                                    Đổi mật khẩu
                                </Button>
                            </Form.Item>
                        </Form>
                        </>
                        )}
                    </>
                ) : (
                    <>
                        <Title level={4} style={{ marginBottom: 8 }}>Phiên đăng nhập</Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                            Thiết bị & đăng nhập - Quản lý các phiên đăng nhập của bạn
                        </Text>

                        <Table
                            columns={columns}
                            dataSource={loginSessions}
                            loading={logLoading}
                            // default*: quá 50 dòng antd tự hiện ô chọn số dòng/trang, ghim pageSize thì ô đó vô tác dụng
                            pagination={{ defaultPageSize: 10 }}
                            style={{ marginTop: 16 }}
                            rowKey={(record) => record.id || record.created_at}
                        />

                        <div style={{ marginTop: 24, padding: '16px', background: '#f9fafb', borderRadius: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <strong>Lưu ý:</strong> Nếu bạn thấy phiên đăng nhập không quen thuộc, hãy đăng xuất ngay và thay đổi mật khẩu để bảo vệ tài khoản.
                            </Text>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}